import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import OverviewTable from "./OveriewTable"
import { styled, Paper, Typography, Box } from "@mui/material"
import OpportunityTable from "./OpportunityTable"
import DoctorsTable from "./DoctorsTable"
import { useResponseHandle } from "../../../hooks"
import {
  setDoctorData,
  setDoctorDataAgg,
  setHygienistsData,
  setHygienistsDataAgg,
  setOpportunityData,
  setOverviewData,
  setOverviewGraphData,
} from "../../../redux/slices/dashboardSlice"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "../../../redux/hooks"
import DashboardAPI from "../../../../api/dashboard"
import { getTokenLocal } from "../../../../helper/utils"
import HygienistsTable from "./HygienistTable"
import moment from "moment"
import { colors } from "../../../../styles/commonStyles"
import { format } from "date-fns"

const getDefaultMonthStartDate = () => {
  const date = new Date()
  date.setDate(1)
  return date
}

export const ReportingFooter = (props: any) => {
  const { column, agg, dollar, percent } = props
  const key = column.columnDef.accessorKey
  // console.log(agg)

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        backgroundColor: colors.darkBlue,
      }}
    >
      <Box sx={{ ...topFooterCellStyles }}>
        {dollar ? "$" : ""}
        {parseFloat(agg?.[key] || "0.00").toLocaleString()}
        {percent ? " %" : ""}
      </Box>
    </Box>
  )
}

export const topFooterCellStyles: React.CSSProperties = {
  borderBottom: "1px solid #fff",
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const dashboardApi = new DashboardAPI()
function DashboardTables() {
  const token = getTokenLocal()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const fetchCacheRef = useRef<Record<string, string>>({
    overview: "",
    opportunities: "",
    doctors: "",
    hygienists: "",
  })
  const tableID = useSelector(
    (state: any) => state.dashboard?.selectedDashboardTab,
  )
  const maevaSelectedGroup = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll?.[0]
  })

  const maevaSelectedPractice = useSelector((state: any) => {
    return state.metric.maevaSelectedPractices?.[0]
  })

  const maevaSelectedRegion = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions?.[0]
  })
  const maevaSelectedDateRange = useSelector((state: any) => {
    return state.metric.maevaSelectedDateRange
  })

  useEffect(() => {
    if (!maevaSelectedGroup?.group_sk) {
      return
    }

    let isCancelled = false

    const runWithCache = async (
      tabKey: keyof typeof fetchCacheRef.current,
      params: Record<string, any>,
      fetcher: () => Promise<void>,
    ) => {
      const cacheKey = JSON.stringify(params)
      if (fetchCacheRef.current[tabKey] === cacheKey) {
        return
      }

      if (isCancelled) {
        return
      }
      setLoading(true)

      try {
        await fetcher()
        fetchCacheRef.current[tabKey] = cacheKey
      } catch (error) {
        if (!isCancelled) {
          console.error(`Error fetching dashboard data for ${tabKey}:`, error)
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    const fetchByActiveTab = async () => {
      const activeTab = tableID?.id || "overview"
      const commonParams = {
        groupSk: maevaSelectedGroup.group_sk,
        regionName: maevaSelectedRegion?.name || null,
        practiceSk: maevaSelectedPractice?.practice_sk || null,
      }

      switch (activeTab) {
        case "overview": {
          await runWithCache("overview", commonParams, async () => {
            await Promise.all([fetchOverviewGraph(), fetchOverviewData()])
          })
          break
        }
        case "opportunities": {
          await runWithCache("opportunities", commonParams, async () => {
            await fetchOpportunityData()
          })
          break
        }
        case "doctors": {
          const defaultStartDateString = format(
            getDefaultMonthStartDate(),
            "MM-dd-yyyy",
          )
          const defaultEndDateString = moment().format("MM-DD-YYYY")
          const doctorParams = {
            ...commonParams,
            startDate:
              maevaSelectedDateRange?.startDate || defaultStartDateString,
            endDate: maevaSelectedDateRange?.endDate || defaultEndDateString,
          }
          await runWithCache("doctors", doctorParams, async () => {
            await fetchDoctorsData()
          })
          break
        }
        case "hygienists": {
          const defaultStartDateString = format(
            getDefaultMonthStartDate(),
            "MM-dd-yyyy",
          )
          const defaultEndDateString = moment().format("MM-DD-YYYY")
          const hygienistParams = {
            ...commonParams,
            startDate:
              maevaSelectedDateRange?.startDate || defaultStartDateString,
            endDate: maevaSelectedDateRange?.endDate || defaultEndDateString,
          }
          await runWithCache("hygienists", hygienistParams, async () => {
            await fetchHygienistsData()
          })
          break
        }
        default:
          break
      }
    }

    fetchByActiveTab()

    return () => {
      isCancelled = true
    }
  }, [
    tableID?.id,
    maevaSelectedGroup?.group_sk,
    maevaSelectedRegion?.name,
    maevaSelectedPractice?.practice_sk,
    maevaSelectedDateRange?.startDate,
    maevaSelectedDateRange?.endDate,
  ])

  const fetchOverviewData = async () => {
    // Guard against missing required parameters
    if (!maevaSelectedGroup?.group_sk) {
      console.warn("Group SK is required for fetchOverviewData")
      return
    }

    const response = await dashboardApi.getOverviewTab(
      token,
      maevaSelectedGroup.group_sk,
      maevaSelectedRegion?.name,
      maevaSelectedPractice?.practice_sk,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        dispatch(
          setOverviewData({
            dailyProductionGoal: [
              {
                metric_name: "Production",
                mtd_budget: result?.data?.today?.production.mtd_budget,
                mtd_actual: result?.data?.today?.production.mtd_actual,
                mtd_variance: result?.data?.today?.production.mtd_variance,
                month_end_goal: result?.data?.today?.production.month_end_goal,
                projected_amount:
                  result?.data?.today?.production.projected_amount,
                projected_variance:
                  result?.data?.today?.production.projected_variance,
                adjusted_daily_goal:
                  result?.data?.today?.production.adjusted_daily_goal,
              },
              {
                metric_name: "Collections(Payments)",
                mtd_budget: result?.data?.today?.collection.mtd_budget,
                mtd_actual: result?.data?.today?.collection.mtd_actual,
                mtd_variance: result?.data?.today?.collection.mtd_variance,
                month_end_goal: result?.data?.today?.collection.month_end_goal,
                projected_amount:
                  result?.data?.today?.collection.projected_amount,
                projected_variance:
                  result?.data?.today?.collection.projected_variance,
                adjusted_daily_goal:
                  result?.data?.today?.collection.adjusted_daily_goal,
              },
            ],
            dailyCollectionGoal: [
              {
                metric_name: "Production",
                ytd_budget: result?.data?.today?.production.ytd_budget,
                ytd_actual: result?.data?.today?.production.ytd_actual,
                ytd_variance: result?.data?.today?.production.ytd_variance,
              },
              {
                metric_name: "Collections(Payments)",
                ytd_budget: result?.data?.today?.collection.ytd_budget,
                ytd_actual: result?.data?.today?.collection.ytd_actual,
                ytd_variance: result?.data?.today?.collection.ytd_variance,
              },
            ],
            yesterday: [
              {
                production_goal: result?.data?.yesterday?.production.goal,
                production_actual: result?.data?.yesterday?.production.actual,
                production_variance:
                  result?.data?.yesterday?.production.variance,

                collection_goal: result?.data?.yesterday?.collection.goal,
                collection_actual: result?.data?.yesterday?.collection.actual,
                collection_variance:
                  result?.data?.yesterday?.collection.variance,
              },
            ],
          }),
        )
      }
    } else {
      navigate("/auth")
    }
  }
  const fetchOpportunityData = async () => {
    // Guard against missing required parameters
    if (!maevaSelectedGroup?.group_sk) {
      console.warn("Group SK is required for fetchOpportunityData")
      return
    }

    const response = await dashboardApi.getOpportunityTab(
      token,
      maevaSelectedGroup.group_sk,
      maevaSelectedRegion?.name || null,
      maevaSelectedPractice?.practice_sk || null,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const summaryData = result?.data?.opportunity_summary.map(
          ({
            opportunity_count,
            name,
          }: {
            opportunity_count: {
              total_count: number
              red_count: number
              green_count: number
            }
            name: string
          }) => ({
            ...opportunity_count,
            name,
          }),
        )
        dispatch(
          setOpportunityData({
            activeOpportunity: result?.data?.active_opportunities,
            activeOpportunitySummary: summaryData,
          }),
        )
      }
    } else {
      navigate("/auth")
    }
  }
  const fetchDoctorsData = async () => {
    dispatch(setDoctorData([]))
    dispatch(setDoctorDataAgg([]))
    const defaultStartDateString = format(
      getDefaultMonthStartDate(),
      "MM-dd-yyyy",
    )
    const defaultEndDateString = moment().format("MM-DD-YYYY")
    const response = await dashboardApi.getDoctorsTab(
      token,
      maevaSelectedGroup?.group_sk,
      maevaSelectedRegion?.name || null,
      maevaSelectedPractice?.practice_sk || null,
      maevaSelectedDateRange?.startDate || defaultStartDateString,
      maevaSelectedDateRange?.endDate || defaultEndDateString,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        dispatch(setDoctorData(result?.data?.provider_list))
        dispatch(setDoctorDataAgg(result?.data?.total))
      }
    } else {
      navigate("/auth")
    }
  }

  const fetchHygienistsData = async () => {
    dispatch(setHygienistsData([]))
    dispatch(setHygienistsDataAgg([]))
    const defaultStartDateString = format(
      getDefaultMonthStartDate(),
      "MM-dd-yyyy",
    )
    const defaultEndDateString = moment().format("MM-DD-YYYY")
    const response = await dashboardApi.getHygienistsTab(
      token,
      maevaSelectedGroup?.group_sk,
      maevaSelectedRegion?.name || null,
      maevaSelectedPractice?.practice_sk || null,
      maevaSelectedDateRange?.startDate || defaultStartDateString,
      maevaSelectedDateRange?.endDate || defaultEndDateString,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        dispatch(setHygienistsData(result?.data?.provider_list))
        dispatch(setHygienistsDataAgg(result?.data?.total))
      }
    } else {
      navigate("/auth")
    }
  }

  const fetchOverviewGraph = async () => {
    // Guard against missing required parameters
    if (!maevaSelectedGroup?.group_sk) {
      console.warn("Group SK is required for fetchOverviewGraph")
      return
    }

    const response = await dashboardApi.getDashboardCharts(
      token,
      maevaSelectedGroup.group_sk,
      maevaSelectedRegion?.name || null,
      maevaSelectedPractice?.practice_sk || null,
    )
    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        dispatch(setOverviewGraphData(result?.data))
      }
    } else {
      navigate("/auth")
    }
  }

  switch (tableID?.id) {
    case "overview": {
      return <OverviewTable loading={loading} />
    }
    case "opportunities": {
      return <OpportunityTable loading={loading} />
    }
    case "doctors": {
      return <DoctorsTable loading={loading} />
    }
    case "hygienists": {
      return <HygienistsTable loading={loading} />
    }
    default:
      return "Not Found"
  }
}

export default DashboardTables

export const TableWrapperPaper = styled(Paper)({
  // margin: "0px 24px",
  // display: "flex",
  flexDirection: "column",
  borderRadius: "4px",
  boxShadow: "0px 2px 8px #00000022",
  height: "fit-content",
  width: "97%",
  // flex: 1,
  // flexGrow: 1,
  "& td ": {
    width: "fit-content !important",
    minWidth: "10px !important",
    whiteSpace: "nowrap !important",
    // maxWidth: "20px !important",
  },
  "& th ": {
    width: "fit-content !important",
    minWidth: "10px !important",
    whiteSpace: "nowrap !important",
    // maxWidth: "20px !important",
  },
})
export const TableHeading = styled(Typography)({
  fontSize: "14px",
  padding: "10px",
  fontWeight: "600",
})
function setHygienistData(data: any): any {
  throw new Error("Function not implemented.")
}
